import { t } from "i18next";
import { match } from "ts-pattern";
import { z } from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  longTextSchema,
  nonFutureDateTimeSchema,
  valueAndLabelSchemaDeprecated,
  yesNoSchema,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";
import { getSupportedIncidentTypeSelection } from "../../workflowLogic/helpers/getIncidentTypeSelection.js";
import { getWorkflowTranslationContext } from "../../workflowLogic/helpers/getWorkflowTranslationContext.js";

export const formSpecification = new MultiPageFormBuilder()
  .withReadonlyFieldAlwaysVisible({
    fieldName: "endIncidentBanner",
    type: "banner",
    body: ({ incidentWorkItem }) => ({
      translationKey: tk("preEndIncident.warningBanner"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    bannerType: "error",
  })

  .withFieldConditionallyVisible({
    fieldName: "whyEndingMcaForm",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "startedFormInError" as const,
          "personHadCapacity" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("preEndIncident.whyEndingMcaForm.title"),
    requiredLabel: tk("preEndIncident.whyEndingMcaForm.required"),
    options: [
      {
        label: tk("preEndIncident.whyEndingMcaForm.options.startedFormInError"),
        value: "startedFormInError",
      },
      {
        label: tk("preEndIncident.whyEndingMcaForm.options.personHadCapacity"),
        value: "personHadCapacity",
      },
      {
        label: tk("preEndIncident.whyEndingMcaForm.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () => true)
        .with("136", () => false)
        .with(undefined, () => false)
        .exhaustive(),
  })

  .withFieldConditionallyVisible({
    fieldName: "whyEndingMcaFormOther",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    label: tk("preEndIncident.whyEndingMcaFormOther.title"),
    multiline: true,
    rows: "5",
    requiredLabel: tk("preEndIncident.whyEndingMcaFormOther.required"),
    isVisible: ({ formValues }) =>
      formValues.whyEndingMcaForm?.value === "other",
  })

  .withFieldConditionallyVisible({
    fieldName: "endingReason",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "startedFormInError" as const,
          "formExpired" as const,
          "nhsNotEngage" as const,
          "medicalExamination" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk("preEndIncident.endingReason.options.startedFormInError"),
        value: "startedFormInError",
      },
      {
        label: tk("preEndIncident.endingReason.options.formExpired"),
        value: "formExpired",
      },
      {
        label: tk("preEndIncident.endingReason.options.nhsNotEngage"),
        value: "nhsNotEngage",
      },
      {
        label: tk("preEndIncident.endingReason.options.medicalExamination"),
        value: "medicalExamination",
      },
      {
        label: tk("preEndIncident.endingReason.options.other"),
        value: "other",
      },
    ],
    label: tk("preEndIncident.endingReason.title"),
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () => false)
        .with("136", () => true)
        .with(undefined, () => true)
        .exhaustive(),
  })

  .withFieldConditionallyVisible({
    fieldName: "additionalEndingReason",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) => formValues.endingReason?.value === "other",
    label: tk("preEndIncident.endingReason.additionalInformation"),
    multiline: true,
    rows: "5",
  })
  .withFieldConditionallyVisible({
    fieldName: "whyFormExpired",
    type: "multi-select",
    schema: z
      .array(
        valueAndLabelSchemaDeprecated(
          z.enum([
            "noBedAvailable" as const,
            "delayedAssessment" as const,
            "processProblems" as const,
          ]),
        ),
      )
      .min(1, "At least one option must be selected."),
    isVisible: ({ formValues }) =>
      formValues.endingReason?.value === "formExpired",
    label: tk("preEndIncident.whyFormExpired.title"),
    options: [
      {
        label: tk("preEndIncident.whyFormExpired.options.noBedAvailable"),
        value: "noBedAvailable",
      },
      {
        label: tk("preEndIncident.whyFormExpired.options.delayedAssessment"),
        value: "delayedAssessment",
      },
      {
        label: tk("preEndIncident.whyFormExpired.options.processProblems"),
        value: "processProblems",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "otherProcessProblemsReason",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    multiline: true,
    rows: "5",
    isVisible: ({ formValues }) =>
      (formValues.whyFormExpired ?? []).some(
        (option) => option.value === "processProblems",
      ),
    label: tk("preEndIncident.whyFormExpired.otherProcessProblems"),
  })
  .withFieldConditionallyVisible({
    fieldName: "whyNhsNotEngage",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "internetConnectivityIssue" as const,
          "emailNotReceived" as const,
          "handoverCompletedVerbally" as const,
          "usedLocalForm" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.endingReason?.value === "nhsNotEngage",
    label: tk("preEndIncident.whyNhsNotEngage.title"),
    options: [
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.internetConnection"),
        value: "internetConnectivityIssue",
      },
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.emailNotReceived"),
        value: "emailNotReceived",
      },
      {
        label: tk(
          "preEndIncident.whyNhsNotEngage.options.handoverCompletedVerbally",
        ),
        value: "handoverCompletedVerbally",
      },
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.localForm"),
        value: "usedLocalForm",
      },
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.other"),
        value: "other",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "additionalInformationNotEngage",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.whyNhsNotEngage?.value === "other",
    label: tk("preEndIncident.whyNhsNotEngage.additionalInformationNotEngage"),
    multiline: true,
    rows: "5",
  })
  .withFieldConditionallyVisible({
    fieldName: "wasAssessedMentalHealthPlaceOfSafety",
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    isVisible: ({ formValues }) =>
      formValues.whyNhsNotEngage?.value === "emailNotReceived" ||
      formValues.whyNhsNotEngage?.value === "internetConnectivityIssue" ||
      formValues.whyNhsNotEngage?.value === "usedLocalForm" ||
      formValues.whyNhsNotEngage?.value === "handoverCompletedVerbally" ||
      formValues.whyNhsNotEngage?.value === "other" ||
      formValues.endingReason?.value === "other",
    label: tk("preEndIncident.wasAssessedMentalHealthPlaceOfSafety"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })

  .withFieldConditionallyVisible({
    fieldName: "didHandoverOccur",
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    isVisible: ({ formValues }) =>
      formValues.wasAssessedMentalHealthPlaceOfSafety?.value === "yes",
    label: tk("preEndIncident.hasHandoverOccurred"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "handoverOccur",
    type: "date-time",
    ...new FieldBuilder().withNullableSchema(nonFutureDateTimeSchema).build(),
    dateLabel: tk("preEndIncident.incidentDateHandover"),
    timeLabel: tk("preEndIncident.incidentTimeHandover"),
    disableFuture: true,
    isVisible: ({ formValues }) =>
      formValues.wasAssessedMentalHealthPlaceOfSafety?.value === "yes" &&
      formValues.didHandoverOccur?.value === "yes",
  })
  .withFieldConditionallyVisible({
    fieldName: "leavePos",
    type: "date-time",
    ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
    dateLabel: tk("endIncidentCommon.incidentLeaveDatePos"),
    timeLabel: tk("endIncidentCommon.incidentLeaveTimePos"),
    disableFuture: true,
    isVisible: ({ formValues }) =>
      formValues.wasAssessedMentalHealthPlaceOfSafety?.value === "yes" &&
      formValues.didHandoverOccur?.value === "yes",
  })
  .withFieldConditionallyVisible({
    fieldName: "outcomeOfAssessment",
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("preEndIncident.outcomeOfAssessment"),
    isVisible: ({ formValues }) =>
      formValues.wasAssessedMentalHealthPlaceOfSafety?.value === "yes",
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "selectedAssessmentOutcome",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum(["discharged" as const, "admitted" as const, "other" as const]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.outcomeOfAssessment?.value === "yes",
    label: tk("preEndIncident.selectedAssessmentOutcome.title"),
    options: [
      {
        label: tk(
          "preEndIncident.selectedAssessmentOutcome.options.discharged",
        ),
        value: "discharged",
      },
      {
        label: tk("preEndIncident.selectedAssessmentOutcome.options.admitted"),
        value: "admitted",
      },
      {
        label: tk("preEndIncident.selectedAssessmentOutcome.options.other"),
        value: "other",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "selectedAssessmentOutcomeOther",
    type: "textbox",
    label: tk("finishForm.whatWasTheOutcome.otherOutcome"),
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.selectedAssessmentOutcome?.value === "other",
    multiline: true,
    rows: "5",
  })
  .withFieldConditionallyVisible({
    fieldName: "furtherCareType",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "furtherDetention" as const,
          "voluntary" as const,
          "unknown" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.selectedAssessmentOutcome?.value === "admitted",
    label: tk("preEndIncident.furtherCareType.title"),
    options: [
      {
        label: tk("preEndIncident.furtherCareType.options.furtherDetention"),
        value: "furtherDetention",
      },
      {
        label: tk("preEndIncident.furtherCareType.options.voluntary"),
        value: "voluntary",
      },
      {
        label: tk("preEndIncident.furtherCareType.options.unknown"),
        value: "unknown",
      },
    ],
  })
  .withPage(["endIncidentBanner"])
  .withPage(["whyEndingMcaForm", "whyEndingMcaFormOther"])
  .withPage(["endingReason", "additionalEndingReason"])
  .withPage(["whyFormExpired", "otherProcessProblemsReason"])
  .withPage(["whyNhsNotEngage", "additionalInformationNotEngage"])
  .withPage(["wasAssessedMentalHealthPlaceOfSafety"])
  .withPage(["didHandoverOccur", "handoverOccur"])
  .withPage(["leavePos"])
  .withPage(["outcomeOfAssessment"])
  .withPage([
    "selectedAssessmentOutcome",
    "furtherCareType",
    "selectedAssessmentOutcomeOther",
  ])
  .build({ t });

export type PreEndIncidentFormData = ReturnType<
  typeof formSpecification
>["formType"];
